import React, { useState, useEffect } from "react";
import api from '../Services/api';

interface DashboardStats {
  users: {
    total: number;
    activeSubscriptions: number;
    newToday: number;
  };
  logs: {
    total: number;
    authEvents: number;
    subscriptionEvents: number;
    classificationEvents: number;
    recentErrors: {
      type: string;
      event: string;
      details: any;
      timestamp: string;
      userEmail: string;
    }[];
  };
  contactForms: {
    total: number;
    pending: number;
    resolved: number;
  };
}

export const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await api.get(`/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return <div className="p-6 text-gray-900 dark:text-gray-100">Loading dashboard...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-gray-100">Dashboard Overview</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* User Stats */}
        <div className="bg-white dark:bg-neutral-darker p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">User Statistics</h3>
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <div className="flex justify-between">
              <span>Total Users:</span>
              <span className="font-semibold">{stats?.users.total}</span>
            </div>
            <div className="flex justify-between">
              <span>Active Subscriptions:</span>
              <span className="font-semibold">{stats?.users.activeSubscriptions}</span>
            </div>
            <div className="flex justify-between">
              <span>New Users Today:</span>
              <span className="font-semibold">{stats?.users.newToday}</span>
            </div>
          </div>
        </div>

        {/* System Logs */}
        <div className="bg-white dark:bg-neutral-darker p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">System Logs</h3>
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <div className="flex justify-between">
              <span>Total Events:</span>
              <span className="font-semibold">{stats?.logs.total}</span>
            </div>
            <div className="flex justify-between">
              <span>Auth Events:</span>
              <span className="font-semibold">{stats?.logs.authEvents}</span>
            </div>
            <div className="flex justify-between">
              <span>Subscription Events:</span>
              <span className="font-semibold">{stats?.logs.subscriptionEvents}</span>
            </div>
            <div className="flex justify-between">
              <span>Classification Events:</span>
              <span className="font-semibold">{stats?.logs.classificationEvents}</span>
            </div>
          </div>
        </div>

        {/* Contact Forms */}
        <div className="bg-white dark:bg-neutral-darker p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Contact Forms</h3>
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <div className="flex justify-between">
              <span>Total Submissions:</span>
              <span className="font-semibold">{stats?.contactForms.total}</span>
            </div>
            <div className="flex justify-between">
              <span>Pending:</span>
              <span className="font-semibold">{stats?.contactForms.pending}</span>
            </div>
            <div className="flex justify-between">
              <span>Resolved:</span>
              <span className="font-semibold">{stats?.contactForms.resolved}</span>
            </div>
          </div>
        </div>

        {/* Error Logs */}
        <div className="col-span-full bg-white dark:bg-neutral-darker p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Recent Errors</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-gray-700 dark:text-gray-300">
                  <th className="pb-3">Time</th>
                  <th className="pb-3">Type</th>
                  <th className="pb-3">Event</th>
                  <th className="pb-3">User</th>
                  <th className="pb-3">Details</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 dark:text-gray-400">
                {stats?.logs.recentErrors.map((error, index) => (
                  <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                    <td className="py-3">{new Date(error.timestamp).toLocaleString()}</td>
                    <td className="py-3">{error.type}</td>
                    <td className="py-3">{error.event}</td>
                    <td className="py-3">{error.userEmail}</td>
                    <td className="py-3">
                      <pre className="whitespace-pre-wrap text-sm">
                        {JSON.stringify(error.details, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
